/**
 * Student Admin Data Form
 * One-time entry for student biometric and vehicle attributes
 * Data persists and auto-populates in coordination submissions
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Card,
  CardContent,
  CardHeader,
  Alert,
  CircularProgress,
  Stack,
  Chip,
  useTheme,
  alpha,
} from '@mui/material';
import {
  Save as SaveIcon,
  Person as PersonIcon,
  DirectionsCar as CarIcon,
  Edit as EditIcon,
  CheckCircle as CheckIcon,
} from '@mui/icons-material';
import * as itineraryApi from '../services/itineraryApi';
import { useAuth } from '@signals/app-shell/hooks/useAuth';
import { useParticipantLabels } from '@signals/app-shell/hooks/useParticipantLabels';
import { useStudentProfileStore } from '../stores/studentProfileStore';

const StudentAdminForm: React.FC = () => {
  const { user, loading: authLoading } = useAuth();
  const { singular, singularLower } = useParticipantLabels();
  const syncProfile = useStudentProfileStore((state) => state.syncProfile);
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  
  // Form state
  const [studentNumber, setStudentNumber] = useState<string>('');
  const [gender, setGender] = useState<string>('');
  const [height, setHeight] = useState<string>('');
  const [weight, setWeight] = useState<string>('');
  const [hairTypeColor, setHairTypeColor] = useState<string>('');
  const [facialHairTypeColor, setFacialHairTypeColor] = useState<string>('');
  const [vehMake, setVehMake] = useState<string>('');
  const [vehModel, setVehModel] = useState<string>('');
  const [vehColor, setVehColor] = useState<string>('');
  const [vehPlate, setVehPlate] = useState<string>('');
  
  // Choice lists
  const [choiceLists, setChoiceLists] = useState<itineraryApi.ChoiceLists>({
    student_numbers: [],
    route_types: [],
    route_components: [],
    genders: [],
  });
  
  // UI state
  const [loading, setLoading] = useState<boolean>(false);
  const [loadingProfile, setLoadingProfile] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [existingProfile, setExistingProfile] = useState<boolean>(false);
  const [_isEditMode, setIsEditMode] = useState<boolean>(false);
  
  // Check if user is a student or trainee (for RBAC)
  // Check both role and username pattern (username might contain "Student" or "Trainee")
  const isStudent = user?.roles?.includes('student');
  const usernameLower = user?.username?.toLowerCase() || '';
  const isTrainee = usernameLower.includes('trainee');
  const isStudentInUsername = usernameLower.includes('student');
  const isStudentOrTrainee = isStudent || isTrainee || isStudentInUsername;
  
  // Helper function to extract participant number from username
  // Handles both "Student-X" and "Trainee-X" formats
  const extractStudentNumber = (username: string): string | null => {
    if (!username) return null;
    
    // Try to match Student-X or Trainee-X patterns
    // Examples: Student5@ht.qfusion.us -> Student-05, Trainee3@ht.qfusion.us -> Trainee-03
    const studentMatch = username.match(/Student[_-]?(\d+)/i);
    const traineeMatch = username.match(/Trainee[_-]?(\d+)/i);
    
    if (traineeMatch) {
      return `Trainee-${traineeMatch[1].padStart(2, '0')}`;
    }
    if (studentMatch) {
      return `Student-${studentMatch[1].padStart(2, '0')}`;
    }
    
    return null;
  };
  
  // Load existing profile when student is selected
  const handleStudentSelect = useCallback(async (studentNum: string) => {
    setStudentNumber(studentNum);
    setError(null);
    setSuccess(null);
    
    if (!studentNum) {
      // Clear form
      setGender('');
      setHeight('');
      setWeight('');
      setHairTypeColor('');
      setFacialHairTypeColor('');
      setVehMake('');
      setVehModel('');
      setVehColor('');
      setVehPlate('');
      setExistingProfile(false);
      setIsEditMode(false);
      return;
    }
    
    setLoadingProfile(true);
    
    try {
      const profile = await itineraryApi.getStudentProfile(studentNum);
      
      // Profile exists - load it
      setGender(profile.gender || '');
      setHeight(profile.height || '');
      setWeight(profile.weight || '');
      setHairTypeColor(profile.hair_typecolor || '');
      setFacialHairTypeColor(profile.facial_hair_typecolor || '');
      setVehMake(profile.veh_make || '');
      setVehModel(profile.veh_model || '');
      setVehColor(profile.veh_color || '');
      setVehPlate(profile.veh_plate || '');
      
      setExistingProfile(true);
      setIsEditMode(true);
      setSuccess(`${singular} profile loaded for ${studentNum}. You can update the information below.`);
    } catch (err: any) {
      if (err.response?.status === 404) {
        // No profile found - new entry mode
        setGender('');
        setHeight('');
        setWeight('');
        setHairTypeColor('');
        setFacialHairTypeColor('');
        setVehMake('');
        setVehModel('');
        setVehColor('');
        setVehPlate('');
        setExistingProfile(false);
        setIsEditMode(false);
        setError(null);
      } else {
        setError('Failed to load profile. Please try again.');
      }
    } finally {
      setLoadingProfile(false);
    }
  }, []);
  
  // Load choice lists on mount and auto-populate for students/trainees
  useEffect(() => {
    // Don't try to auto-populate if auth is still loading or user is not available
    if (authLoading || !user) {
      return;
    }
    
    const loadChoiceLists = async () => {
      try {
        const lists = await itineraryApi.getChoiceLists();
        setChoiceLists(lists);
        
        // If user is a student or trainee, auto-select their student number
        if (isStudentOrTrainee && user?.username) {
          const studentNum = extractStudentNumber(user.username);
          console.log('Auto-populating for student/trainee:', {
            username: user.username,
            extractedStudentNum: studentNum,
            isInList: studentNum ? lists.student_numbers.includes(studentNum) : false,
            availableNumbers: lists.student_numbers.slice(0, 5) // First 5 for debugging
          });
          
          if (studentNum && lists.student_numbers.includes(studentNum)) {
            // Auto-select and load profile
            handleStudentSelect(studentNum);
          } else if (studentNum) {
            console.warn(`${singular} number ${studentNum} not found in choice lists`);
            setError(`${singular} number ${studentNum} not found in available options. Please contact support.`);
          }
        }
      } catch (err: any) {
        console.error('Error loading choice lists:', err);
        setError('Failed to load form options');
      }
    };
    
    loadChoiceLists();
  }, [authLoading, isStudentOrTrainee, user, handleStudentSelect]);
  
  // Submit form
  const handleSubmit = async () => {
    setLoading(true);
    setError(null);
    setSuccess(null);
    
    // Validation
    if (!studentNumber) {
      setError(`Please select a ${singularLower} number`);
      setLoading(false);
      return;
    }
    
    if (!gender) {
      setError('Gender is required');
      setLoading(false);
      return;
    }
    
    if (!height || !weight || !hairTypeColor || !facialHairTypeColor) {
      setError('All admin data fields are required');
      setLoading(false);
      return;
    }
    
    try {
      const profileData: itineraryApi.StudentProfile = {
        student_number: studentNumber,
        gender,
        height,
        weight,
        hair_typecolor: hairTypeColor,
        facial_hair_typecolor: facialHairTypeColor,
        veh_make: vehMake,
        veh_model: vehModel,
        veh_color: vehColor,
        veh_plate: vehPlate,
      };
      
      const savedProfile = existingProfile
        ? await itineraryApi.updateStudentProfile(studentNumber, profileData)
        : await itineraryApi.createStudentProfile(profileData);

      syncProfile(savedProfile);

      if (existingProfile) {
        setSuccess(`✅ ${singular} profile updated successfully for ${studentNumber}! This data will auto-populate in coordination submissions.`);
      } else {
        setSuccess(`✅ ${singular} profile created successfully for ${studentNumber}! This data will auto-populate in coordination submissions.`);
        setExistingProfile(true);
        setIsEditMode(true);
      }
      
    } catch (err: any) {
      console.error('Error saving profile:', err);
      if (err.response?.status === 400 && err.response?.data?.detail?.includes('already exists')) {
        setError('Profile already exists. Try updating instead.');
        setExistingProfile(true);
        setIsEditMode(true);
      } else {
        setError(err.response?.data?.detail || 'Failed to save profile. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <PersonIcon color="primary" />
        {singular} Admin Data
      </Typography>
      
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Enter your information once. It will auto-populate in all future coordination submissions.
      </Typography>
      
      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      
      {success && (
        <Alert severity="success" onClose={() => setSuccess(null)} sx={{ mb: 2 }} icon={<CheckIcon />}>
          {success}
        </Alert>
      )}
      
      {/* Participant selection */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <FormControl fullWidth size="small">
            <InputLabel>{`${singular} Number *`}</InputLabel>
            <Select
              value={studentNumber}
              onChange={(e) => handleStudentSelect(e.target.value)}
              label={`${singular} Number *`}
              disabled={loadingProfile || isStudentOrTrainee}
            >
              <MenuItem value="">
                <em>{`Select your ${singularLower} number`}</em>
              </MenuItem>
              {choiceLists.student_numbers
                .filter(num => {
                  // If user is a student or trainee, only show their own student number
                  if (isStudentOrTrainee) {
                    // Extract student number from username
                    if (user?.username) {
                      const userStudentNum = extractStudentNumber(user.username);
                      return userStudentNum ? num === userStudentNum : false;
                    }
                    // If we can't determine student number, don't show any
                    return false;
                  }
                  // Non-students see all participant numbers
                  return true;
                })
                .map((num) => (
                  <MenuItem key={num} value={num}>
                    {num}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
          
          {existingProfile && (
            <Box sx={{ mt: 2 }}>
              <Chip 
                icon={<CheckIcon />} 
                label="Profile Exists - Editing Mode" 
                color="success" 
                size="small" 
              />
            </Box>
          )}
        </CardContent>
      </Card>
      
      {/* Admin Data Section */}
      {studentNumber && (
        <Card sx={{ mb: 2 }}>
          <CardHeader
            avatar={<PersonIcon color="secondary" />}
            title="Admin Data (Biometric Attributes)"
            subheader={loadingProfile ? 'Loading...' : existingProfile ? 'Update your information below' : 'Enter your information'}
            titleTypographyProps={{ variant: 'subtitle1' }}
          />
          <CardContent>
            <Stack spacing={2}>
              <FormControl fullWidth size="small" required>
                <InputLabel>Gender *</InputLabel>
                <Select
                  value={gender}
                  onChange={(e) => setGender(e.target.value)}
                  label="Gender *"
                  disabled={loadingProfile}
                >
                  {choiceLists.genders.map((g) => (
                    <MenuItem key={g} value={g}>
                      {g}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              
              <Stack direction="row" spacing={2}>
                <TextField
                  label="Height *"
                  value={height}
                  onChange={(e) => setHeight(e.target.value)}
                  size="small"
                  fullWidth
                  required
                  disabled={loadingProfile}
                  placeholder="e.g., 6'2&quot;"
                  helperText="Include units"
                />
                
                <TextField
                  label="Weight *"
                  value={weight}
                  onChange={(e) => setWeight(e.target.value)}
                  size="small"
                  fullWidth
                  required
                  disabled={loadingProfile}
                  placeholder="e.g., 180 lbs"
                  helperText="Include units"
                />
              </Stack>
              
              <TextField
                label="Hair (Type/Color) *"
                value={hairTypeColor}
                onChange={(e) => setHairTypeColor(e.target.value)}
                size="small"
                fullWidth
                required
                disabled={loadingProfile}
                placeholder="e.g., Short/Brown"
              />
              
              <TextField
                label="Facial Hair (Type/Color) *"
                value={facialHairTypeColor}
                onChange={(e) => setFacialHairTypeColor(e.target.value)}
                size="small"
                fullWidth
                required
                disabled={loadingProfile}
                placeholder="e.g., Beard/Black or None"
              />
            </Stack>
          </CardContent>
        </Card>
      )}
      
      {/* Vehicle Data Section */}
      {studentNumber && (
        <Card sx={{ mb: 2 }}>
          <CardHeader
            avatar={<CarIcon color="info" />}
            title="Vehicle Data (Optional)"
            subheader="Enter vehicle information if applicable"
            titleTypographyProps={{ variant: 'subtitle1' }}
          />
          <CardContent>
            <Stack spacing={2}>
              <Stack direction="row" spacing={2}>
                <TextField
                  label="Make"
                  value={vehMake}
                  onChange={(e) => setVehMake(e.target.value)}
                  size="small"
                  fullWidth
                  disabled={loadingProfile}
                  placeholder="e.g., Toyota"
                />
                
                <TextField
                  label="Model"
                  value={vehModel}
                  onChange={(e) => setVehModel(e.target.value)}
                  size="small"
                  fullWidth
                  disabled={loadingProfile}
                  placeholder="e.g., Camry"
                />
              </Stack>
              
              <Stack direction="row" spacing={2}>
                <TextField
                  label="Color"
                  value={vehColor}
                  onChange={(e) => setVehColor(e.target.value)}
                  size="small"
                  fullWidth
                  disabled={loadingProfile}
                  placeholder="e.g., Silver"
                />
                
                <TextField
                  label="License Plate"
                  value={vehPlate}
                  onChange={(e) => setVehPlate(e.target.value)}
                  size="small"
                  fullWidth
                  disabled={loadingProfile}
                  placeholder="e.g., ABC1234"
                />
              </Stack>
            </Stack>
          </CardContent>
        </Card>
      )}
      
      {/* Information Box */}
      {studentNumber && (
        <Alert severity="info" sx={{ mb: 2 }}>
          <Typography variant="body2" gutterBottom>
            <strong>How it works:</strong>
          </Typography>
          <Typography variant="body2" component="div">
            • This data is stored for the period of your course<br />
            • It will automatically fill admin data in coordination submission forms<br />
            • You will need to update as necessary information changes<br />
          </Typography>
        </Alert>
      )}
      
      {/* Submit Button */}
      {studentNumber && (
        <Button
          variant="contained"
          color="primary"
          size="large"
          fullWidth
          startIcon={loading ? <CircularProgress size={20} color="inherit" /> : existingProfile ? <EditIcon /> : <SaveIcon />}
          onClick={handleSubmit}
          disabled={loading || loadingProfile}
        >
          {loading ? 'Saving...' : existingProfile ? 'Update Profile' : 'Save Profile'}
        </Button>
      )}
      
      {/* Empty State */}
      {!studentNumber && (
        <Card sx={{ 
          bgcolor: isDark ? alpha(theme.palette.background.paper, 0.6) : alpha(theme.palette.grey[100], 0.8),
          border: `1px solid ${theme.palette.divider}`,
          mt: 2 
        }}>
          <CardContent sx={{ textAlign: 'center', py: 4 }}>
            <PersonIcon sx={{ fontSize: 64, color: theme.palette.text.disabled, mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              {`Select Your ${singular} Number`}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {`Choose your ${singularLower} number from the dropdown above to get started`}
            </Typography>
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default StudentAdminForm;
